import { cn } from '../../../utils/cn';
import { Icon } from '../Icon/Icon';
import type { ReactNode } from 'react';

/** Label + control + hint/error. Every form control uses this for consistent spacing. */
export interface FieldProps {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children?: ReactNode;
  labelAction?: ReactNode;
}

/** Props every labelled form control accepts. */
export type FieldOwnProps = Pick<FieldProps, 'label' | 'hint' | 'error' | 'required' | 'labelAction'> & { containerClassName?: string };

export function Field({ label, htmlFor, hint, error, required, className, children, labelAction }: FieldProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {(label || labelAction) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <label htmlFor={htmlFor} className="text-[13px] font-medium text-navy-700">
              {label}
              {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
            </label>
          )}
          {labelAction}
        </div>
      )}
      {children}
      {error ? (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} className="flex items-start gap-1 text-[13px] leading-snug text-red-600">
          <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
          {error}
        </p>
      ) : (
        hint && (
          <p id={htmlFor ? `${htmlFor}-hint` : undefined} className="text-[13px] leading-snug text-ink-400">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/** Shared look for input-like controls (input, select trigger, date trigger, textarea). */
export function controlClass({ error, disabled, open }: { error?: unknown; disabled?: boolean; open?: boolean } = {}) {
  return cn(
    'w-full rounded-lg border bg-white text-navy-900 transition-[border-color,box-shadow] placeholder:text-ink-300 outline-none',
    error
      ? 'border-red-500 focus:ring-4 focus:ring-red-100'
      : cn('border-line hover:border-navy-300 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15', open && 'border-brand-500 ring-4 ring-brand-500/15'),
    disabled && 'cursor-not-allowed bg-navy-50/70 text-ink-400 hover:border-line',
  );
}

export const describedBy = (id: string, error?: unknown, hint?: unknown) => (error ? `${id}-error` : hint ? `${id}-hint` : undefined);

export default Field;
